import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    // yunari (journey w5): if focus fell back to <body> while the drawer is open, Escape still closes it.
    document.addEventListener('keyup', this.#onDocumentKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('keyup', this.#onDocumentKeyUp);
  }

  /** @param {KeyboardEvent} event */
  #onDocumentKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#close(this.refs.details);
  };

  /**
   * yunari (journey w5): a submenu panel always starts at its top (heading and first link visible).
   * A stale scroll offset from an earlier visit made a tap land on the wrong row.
   * @param {Element} details
   */
  #resetPanelScroll(details) {
    details
      .querySelectorAll(':scope > .menu-drawer__submenu, :scope > .menu-drawer__submenu .menu-drawer__inner-submenu')
      .forEach((el) => {
        el.scrollTop = 0;
      });
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.isOpen) return;

    // yunari: accordion <details> inside the drawer are not drawers. Escape closes the open
    // submenu panel if there is one, otherwise the whole drawer (no dead second Escape).
    const target = event.target instanceof Element ? event.target : null;
    const submenu = target?.closest('details.menu-open');
    this.#close(submenu && submenu !== this.refs.details ? submenu : this.refs.details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) this.#openedAt = performance.now();

    // yunari (nav w6 r3): a panel row is opened by us, not by the native <details> toggle. A tap on a row whose panel
    // is still sliding out (quick "Zurück" then the same row again) used to toggle it shut natively, and the tap was lost.
    // The pending close sees .menu-open again and leaves the panel alone (see #close).
    if (details !== this.refs.details) {
      if (event?.cancelable) event.preventDefault();
      details.open = true;
    }

    summary.setAttribute('aria-expanded', 'true');
    if (details !== this.refs.details && !details.classList.contains('menu-open')) this.#resetPanelScroll(details);

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (details !== this.refs.details) details.open = true;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // yunari (nav w6): the row that opened a panel is now covered, so focus moves to the panel's "Zurück"
      // (keyboard and screen-reader users keep their place). Was an inline script in header-drawer.liquid that
      // never bound on the live page.
      if (details !== this.refs.details) {
        // yunari (nav w10 r3): everything behind the open panel (root rows, product strip, account links, the row's own
        // summary) leaves the tab order and the accessibility tree while the panel is open.
        this.#inertOutside(details.querySelector(':scope > .menu-drawer__submenu'));
        details
          .querySelector(':scope > .menu-drawer__submenu .menu-drawer__back-button')
          ?.focus({ preventScroll: true });
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /** yunari (nav w10 r3): elements made inert while a panel is open. @type {Element[]} */
  #inerted = [];

  /**
   * yunari (nav w10 r3): make every sibling on the path from the open panel up to the drawer inert, so keyboard and
   * screen-reader users only meet the panel (the panel itself is nested inside the root list, so the list cannot be
   * made inert as a whole).
   * @param {Element | null} panel
   */
  #inertOutside(panel) {
    this.#clearInert();
    const stop = this.refs.menuDrawer;
    if (!panel || !stop || !stop.contains(panel)) return;
    let node = panel;
    while (node && node !== stop && node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || sibling.inert || sibling.tagName === 'SCRIPT' || sibling.tagName === 'STYLE') continue;
        sibling.inert = true;
        this.#inerted.push(sibling);
      }
      node = node.parentElement;
    }
  }

  #clearInert() {
    for (const el of this.#inerted) el.inert = false;
    this.#inerted = [];
  }

  /** yunari (nav w8 r3): when the main drawer last opened (performance.now()). */
  #openedAt = 0;

  /**
   * yunari (nav w8 r3): backdrop tap. A second tap right after the hamburger (panel still sliding in) used to land on
   * the backdrop and shut the drawer again; taps during the opening slide are ignored.
   */
  dismiss() {
    if (performance.now() - this.#openedAt < 400) return;
    this.close();
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    // yunari (nav w10 r3): the root list is reachable again before focus returns to its row.
    this.#clearInert();
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // yunari (journey w5): leaving a panel (Zurück or Escape) puts focus on the row that opened it,
    // so the next Escape reaches this drawer and closes it.
    const isSubmenu = details !== this.refs.details;
    // yunari (nav w6): release the leaving panel's focus trap now (it used to last until the slide ended, so a
    // quick tap on another row had its focus pulled back to this row).
    if (isSubmenu) trapFocus(this.refs.details);
    if (isSubmenu) summary.focus({ preventScroll: true });

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        // yunari (nav w6 r3): reopened while it was sliding out: keep it open.
        if (details.classList.contains('menu-open')) return;
        reset(details);
        if (isSubmenu) this.#resetPanelScroll(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          // yunari: give focus back to the hamburger the drawer was opened from.
          if (details.contains(document.activeElement) || document.activeElement === document.body) {
            details.querySelector('summary')?.focus({ preventScroll: true });
          }
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          // yunari (nav w6): if another panel was opened while this one slid out (quick tap after "Zurück"),
          // keep focus and the trap in that panel instead of pulling them back to the root list.
          const other = [...this.querySelectorAll('details.menu-open')].find(
            (d) => d !== this.refs.details && d !== details
          );
          if (other) {
            trapFocus(other);
            if (!other.contains(document.activeElement)) {
              other.querySelector(':scope > .menu-drawer__submenu .menu-drawer__back-button')?.focus({ preventScroll: true });
            }
            return;
          }
          trapFocus(this.refs.details);
          if (!this.refs.details.contains(document.activeElement) || document.activeElement === this.refs.details) {
            summary.focus({ preventScroll: true });
          }
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/*
 * yunari (nav w6 r2): "Anliegen" / "Alle Anliegen" lead to the concern overview on the home page (/#anliegen).
 * On the home page that is an in-page jump, so nothing reloads: close the phone drawer and the open desktop
 * mega menu ourselves, otherwise the menu stays over the section it just scrolled to.
 */
if (!window.__yunariSamePageNav) {
  window.__yunariSamePageNav = true;
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('#header-component a[href*="#"]');
    if (!(link instanceof HTMLAnchorElement) || !link.hash) return;
    if (link.hasAttribute('data-y-opens-menu')) return; // yunari (nav w8 r2): opens its menu instead
    if (link.pathname !== window.location.pathname) return;
    const section = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!section) return;
    event.preventDefault();
    const drawer = link.closest('header-drawer');
    const drawerWasOpen = drawer instanceof HeaderDrawer && drawer.isOpen;
    if (drawerWasOpen) drawer.close();
    const menu = link.closest('header-menu');
    if (menu) {
      link.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // The drawer releases the page scroll when its slide-out ends; jump after that.
    setTimeout(() => {
      if (window.location.hash !== link.hash) history.pushState(null, '', link.hash);
      section.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      // If anything stopped the smooth scroll (a scroll lock being released, a throttled tab), land there anyway.
      setTimeout(() => {
        const margin = parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
        if (Math.abs(section.getBoundingClientRect().top - margin) > 40) {
          section.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      }, 900);
    }, drawerWasOpen ? 360 : 0);
  });
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/*
 * yunari (nav w8 r2): desktop ANLIEGEN has no overview page of its own (its old target, /#anliegen, repeated the seven
 * concerns the mega menu already lists). A click opens the mega menu instead, via its disclosure button, and never
 * closes a menu that hover has already opened.
 */
if (!window.__yunariMenuOpener) {
  window.__yunariMenuOpener = true;
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('#header-component a[data-y-opens-menu]');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
    const disclosure = link.closest('.menu-list__list-item')?.querySelector('[ref="disclosure[]"]');
    if (!(disclosure instanceof HTMLElement)) return;
    event.preventDefault();
    if (disclosure.getAttribute('aria-expanded') !== 'true') disclosure.click();
  });
}
